import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { DateInput, Select, VesselSelect } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { ReferenceSidebarSplitter } from '../parameters/reference-workspaces/ReferenceSidebarChrome';
import { useResizablePanel } from '../parameters/reference-workspaces/useResizablePanel';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { CrewDataTablePrompt, todayIso, WITHIN_DAYS_OPTIONS } from './reportShared';
import type { CrewStatMixItem, CrewStatisticsResponse } from './types';

export type StatsAppliedFilters = {
  vesselId: string;
  withinDays: string;
  periodFrom: string;
  periodTo: string;
};

export type StatsFilterMode = 'vessel' | 'vessel-days' | 'vessel-period' | 'full';

export const formatStatNum = (n: number | null | undefined, digits = 0) => {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export function kpiTone(n: number, warnAt = 1): 'default' | 'warning' | 'danger' {
  if (n <= 0) return 'default';
  if (n >= warnAt * 5) return 'danger';
  return 'warning';
}

export function buildStatLinks(applied: StatsAppliedFilters) {
  return {
    certGaps: applied.vesselId ? `/crew/cert-gaps` : undefined,
    certs: `/crew/certs-expiring`,
    medicals: `/crew/reports/compliance/medicals-due`,
    coe: `/crew/coe-watchlist`,
    relief: `/crew/relief-board`,
    appraisal: `/crew/appraisal-nr`,
    nationality: applied.vesselId ? `/crew/statistics/nationalities` : undefined,
    wage: applied.vesselId ? `/crew/wage-allottee` : undefined,
  };
}

export type StatReportLinks = ReturnType<typeof buildStatLinks>;

export const KpiTile: React.FC<{
  label: string;
  value: string;
  hint?: string;
  to?: string;
  tone?: 'default' | 'warning' | 'danger';
}> = ({ label, value, hint, to, tone = 'default' }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const valueClass =
    tone === 'danger'
      ? isDark ? 'text-red-400' : 'text-red-600'
      : tone === 'warning'
        ? isDark ? 'text-amber-300' : 'text-amber-700'
        : colors.text;

  const body = (
    <>
      <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>{label}</p>
      <p className={cn('mt-1 text-2xl font-semibold tabular-nums', valueClass)}>{value}</p>
      {hint ? <p className={cn('mt-1 text-xs line-clamp-2', colors.textMuted)}>{hint}</p> : null}
    </>
  );

  const className = cn(
    'rounded-xl border p-4 transition-colors',
    colors.surface,
    colors.border,
    to && 'hover:shadow-sm',
  );

  if (to) {
    return (
      <Link to={to} className={className}>
        {body}
      </Link>
    );
  }
  return <div className={className}>{body}</div>;
};

export const HorizontalBars: React.FC<{
  title: string;
  data: CrewStatMixItem[];
  emptyLabel: string;
  className?: string;
}> = ({ title, data, emptyLabel, className }) => {
  const { colors } = useTheme();
  const max = Math.max(...data.map((d) => d.count), 0);
  const total = data.reduce((s, d) => s + d.count, 0);

  return (
    <section
      className={cn('rounded-xl border p-5 flex flex-col min-h-[14rem]', colors.surface, colors.border, className)}
    >
      <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
      <p className={cn('text-xs mt-0.5 mb-4', colors.textMuted)}>
        {total ? `${total.toLocaleString()} total` : emptyLabel}
      </p>
      {!data.length ? (
        <div className={cn('flex-1 flex items-center justify-center text-sm', colors.textMuted)}>{emptyLabel}</div>
      ) : (
        <ul className="space-y-3 flex-1">
          {data.slice(0, 12).map((row) => {
            const pct = max > 0 ? (row.count / max) * 100 : 0;
            return (
              <li key={row.label} className="min-w-0">
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <span className={cn('text-xs truncate', colors.text)}>{row.label}</span>
                  <span className={cn('text-xs tabular-nums shrink-0', colors.textMuted)}>{row.count}</span>
                </div>
                <div className={cn('h-3 rounded-full overflow-hidden', colors.surfaceHover)}>
                  <div className={cn('h-full rounded-full opacity-80', colors.primary)} style={{ width: `${pct}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export const BucketBars: React.FC<{
  title: string;
  data: CrewStatMixItem[];
  summary?: string;
  emptyLabel?: string;
  className?: string;
}> = ({ title, data, summary, emptyLabel = 'No certificates in window', className }) => {
  const { colors } = useTheme();
  const max = Math.max(...data.map((d) => d.count), 0);
  const total = data.reduce((s, d) => s + d.count, 0);
  const summaryText = total
    ? summary ?? `${total.toLocaleString()} certificates in window`
    : emptyLabel;

  return (
    <section
      className={cn('rounded-xl border p-5 flex flex-col min-h-[14rem]', colors.surface, colors.border, className)}
    >
      <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
      <p className={cn('text-xs mt-0.5 mb-4', colors.textMuted)}>{summaryText}</p>
      {!data.length || !total ? (
        <div className={cn('flex-1 flex items-center justify-center text-sm', colors.textMuted)}>
          {emptyLabel}
        </div>
      ) : (
        <div className="flex items-end gap-3 flex-1 min-h-[10rem]">
          {data.map((row) => {
            const heightPct = max > 0 ? (row.count / max) * 100 : 0;
            return (
              <div key={row.label} className="flex-1 flex flex-col items-center gap-1 min-w-0 h-full justify-end">
                <span className={cn('text-xs tabular-nums', colors.textMuted)}>{row.count}</span>
                <div
                  className={cn('w-full rounded-t-md opacity-80', colors.primary, colors.surfaceHover)}
                  style={{ height: `${Math.max(heightPct, row.count > 0 ? 6 : 2)}%` }}
                />
                <span className={cn('text-xs text-center leading-tight truncate w-full', colors.textMuted)}>
                  {row.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export const StatsBucketHeader: React.FC<{ colorClass: string; label: string }> = ({
  colorClass,
  label,
}) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', colorClass)} />
    {label}
  </span>
);

export const StatsCountCell: React.FC<{ value: unknown; emphasize?: boolean }> = ({
  value,
  emphasize,
}) => {
  const { colors } = useTheme();
  const n = typeof value === 'number' ? value : 0;
  if (!n) {
    return <span className={cn('tabular-nums', colors.textMuted)}>—</span>;
  }
  return (
    <span
      className={cn(
        'tabular-nums font-medium',
        emphasize ? 'text-rose-600 dark:text-rose-400' : colors.text,
      )}
    >
      {n}
    </span>
  );
};

export const StatsBucketChart: React.FC<{
  title: string;
  hint?: string;
  data: CrewStatMixItem[];
  total: number;
  emptyLabel: string;
  barClassFor: (label: string) => string;
}> = ({ title, hint, data, total, emptyLabel, barClassFor }) => {
  const { colors } = useTheme();
  const max = Math.max(...data.map((d) => d.count), 1);

  return (
    <section className="flex flex-col min-h-0 flex-[1.15]">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
        {hint ? <span className={cn('text-[11px]', colors.textMuted)}>{hint}</span> : null}
      </div>
      {!total ? (
        <div className={cn('flex-1 flex items-center justify-center text-sm', colors.textMuted)}>
          {emptyLabel}
        </div>
      ) : (
        <div className="flex items-stretch gap-2 flex-1 min-h-[10rem]">
          {data.map((row) => {
            const heightPct = (row.count / max) * 100;
            const share = total ? Math.round((row.count / total) * 100) : 0;
            return (
              <div key={row.label} className="flex-1 flex flex-col min-w-0">
                <div
                  className={cn(
                    'relative flex-1 min-h-[7rem] rounded-lg overflow-hidden flex items-end',
                    colors.surfaceHover,
                  )}
                >
                  <div
                    className={cn('w-full rounded-t-md transition-[height]', barClassFor(row.label))}
                    style={{ height: `${row.count > 0 ? Math.max(heightPct, 8) : 2}%` }}
                    title={`${row.label}: ${row.count} (${share}%)`}
                  />
                </div>
                <div className="mt-2 text-center">
                  <p className={cn('text-sm font-semibold tabular-nums leading-none', colors.text)}>{row.count}</p>
                  <p className={cn('text-[11px] mt-1 truncate', colors.textMuted)}>{row.label}</p>
                  <p className={cn('text-[10px] tabular-nums', colors.textMuted)}>{share}%</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export const StatsRankedBars: React.FC<{
  title: string;
  hint?: string;
  data: CrewStatMixItem[];
  total: number;
  emptyLabel: string;
  barClass?: string;
}> = ({ title, hint, data, total, emptyLabel, barClass = 'bg-sky-500/90' }) => {
  const { colors } = useTheme();
  const shown = data.slice(0, 10);
  const hiddenCount = data.length - shown.length;
  const rest = data.slice(10).reduce((s, r) => s + r.count, 0);
  const rows = rest > 0 ? [...shown, { label: `Other (${hiddenCount})`, count: rest }] : shown;
  const max = Math.max(...rows.map((d) => d.count), 1);

  return (
    <section className="flex flex-col min-h-0 flex-1">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
        {hint ? <span className={cn('text-[11px]', colors.textMuted)}>{hint}</span> : null}
      </div>
      {!rows.length ? (
        <div className={cn('flex-1 flex items-center justify-center text-sm', colors.textMuted)}>
          {emptyLabel}
        </div>
      ) : (
        <ul className="space-y-2.5 flex-1 min-h-0 overflow-auto pr-0.5">
          {rows.map((row, i) => {
            const pct = max > 0 ? (row.count / max) * 100 : 0;
            const share = total ? Math.round((row.count / total) * 100) : 0;
            return (
              <li key={row.label} className="min-w-0">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <span className={cn('text-xs truncate', colors.text)}>
                    <span className={cn('tabular-nums mr-1.5', colors.textMuted)}>{i + 1}</span>
                    {row.label}
                  </span>
                  <span className={cn('text-xs tabular-nums shrink-0', colors.textMuted)}>
                    {row.count}
                    <span className="ml-1.5 opacity-80">{share}%</span>
                  </span>
                </div>
                <div className={cn('h-2 rounded-full overflow-hidden', colors.surfaceHover)}>
                  <div
                    className={cn('h-full rounded-full', barClass)}
                    style={{ width: `${Math.max(pct, row.count > 0 ? 4 : 0)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export const StatsMixPanel: React.FC<{
  metrics: { label: string; value: string }[];
  children: React.ReactNode;
}> = ({ metrics, children }) => {
  const { colors } = useTheme();
  return (
    <aside
      className={cn(
        'h-full min-h-0 flex flex-col overflow-hidden rounded-lg border shadow-sm',
        colors.surface,
        colors.border,
      )}
    >
      <div className={cn('shrink-0 grid grid-cols-3 divide-x border-b', colors.border, colors.divide)}>
        {metrics.map((m) => (
          <div key={m.label} className="px-3 py-3">
            <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>{m.label}</p>
            <p className={cn('mt-0.5 text-xl font-semibold tabular-nums leading-tight', colors.text)}>{m.value}</p>
          </div>
        ))}
      </div>
      <div className="flex-1 min-h-0 flex flex-col gap-5 p-4 overflow-auto">{children}</div>
    </aside>
  );
};

export function useCrewStatisticsQuery(filterMode: StatsFilterMode) {
  const [vesselId, setVesselId] = useState('');
  const [withinDays, setWithinDays] = useState('90');
  const [periodFrom, setPeriodFrom] = useState(todayIso);
  const [periodTo, setPeriodTo] = useState(todayIso);
  const [applied, setApplied] = useState<StatsAppliedFilters | null>(null);

  const listUrl = applied
    ? `/crew/reports/statistics?withinDays=${encodeURIComponent(applied.withinDays)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}${
        applied.vesselId ? `&vesselId=${encodeURIComponent(applied.vesselId)}` : ''
      }`
    : null;

  const { data, error, isLoading, mutate } = useSWR<CrewStatisticsResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const handleSearch = useCallback(() => {
    setApplied({ vesselId, withinDays, periodFrom, periodTo });
  }, [vesselId, withinDays, periodFrom, periodTo]);

  const showDays = filterMode === 'vessel-days' || filterMode === 'full';
  const showPeriod = filterMode === 'vessel-period' || filterMode === 'full';

  return {
    vesselId,
    setVesselId,
    withinDays,
    setWithinDays,
    periodFrom,
    setPeriodFrom,
    periodTo,
    setPeriodTo,
    applied,
    data,
    error,
    isLoading,
    mutate,
    handleSearch,
    showDays,
    showPeriod,
    links: applied ? buildStatLinks(applied) : null,
  };
}

export const StatsPageShell: React.FC<{
  hintTitle: string;
  hintBody: string;
  filterMode: StatsFilterMode;
  trailingActions?: (ctx: {
    applied: StatsAppliedFilters | null;
    data: CrewStatisticsResponse | undefined;
  }) => React.ReactNode;
  children: (ctx: {
    data: CrewStatisticsResponse;
    links: StatReportLinks;
  }) => React.ReactNode;
}> = ({ hintTitle, hintBody, filterMode, trailingActions, children }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const q = useCrewStatisticsQuery(filterMode);

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{ title: hintTitle, body: hintBody }}
        onSearch={q.handleSearch}
        loading={q.isLoading}
        onRefresh={q.applied ? () => q.mutate() : undefined}
        trailingActions={trailingActions?.({ applied: q.applied, data: q.data })}
        context={
          q.data?.vessel?.vesselName
            ? `${q.data.vessel.vesselName}${
                q.showDays ? ` · within ${q.data.withinDays}d` : ''
              }${q.showPeriod ? ` · period ${q.data.periodFrom} → ${q.data.periodTo}` : ''}`
            : q.data && (q.showDays || q.showPeriod)
              ? `${q.showDays ? `Within ${q.data.withinDays}d` : ''}${
                  q.showDays && q.showPeriod ? ' · ' : ''
                }${q.showPeriod ? `period ${q.data.periodFrom} → ${q.data.periodTo}` : ''}`
              : undefined
        }
      >
        <CrewReportFilterField label="Vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={q.vesselId}
            onChange={(e) => q.setVesselId(String(e.target.value))}
            placeholder="All vessels…"
            includeIds={q.vesselId ? [q.vesselId] : undefined}
          />
        </CrewReportFilterField>
        {q.showDays ? (
          <CrewReportFilterField label="Within days" width="sm">
            <Select
              name="withinDays"
              value={q.withinDays}
              onChange={(e) => q.setWithinDays(String(e.target.value))}
              options={WITHIN_DAYS_OPTIONS}
              showClearButton={false}
            />
          </CrewReportFilterField>
        ) : null}
        {q.showPeriod ? (
          <>
            <CrewReportFilterField label="Period from" width="md">
              <DateInput
                name="periodFrom"
                value={q.periodFrom}
                onChange={(e) => q.setPeriodFrom(e.target.value)}
              />
            </CrewReportFilterField>
            <CrewReportFilterField label="Period to" width="md">
              <DateInput name="periodTo" value={q.periodTo} onChange={(e) => q.setPeriodTo(e.target.value)} />
            </CrewReportFilterField>
          </>
        ) : null}
      </CrewReportSearchHeader>

      <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
        {!q.applied ? (
          <CrewDataTablePrompt>Choose filters, then search.</CrewDataTablePrompt>
        ) : q.error ? (
          <CrewDataTablePrompt>
            <span className={isDark ? 'text-red-300' : 'text-red-600'}>Could not load statistics.</span>
          </CrewDataTablePrompt>
        ) : q.isLoading && !q.data ? (
          <CrewDataTablePrompt>Loading…</CrewDataTablePrompt>
        ) : q.data && q.links ? (
          children({ data: q.data, links: q.links })
        ) : null}
      </div>
    </div>
  );
};

export type StatsListTab<T extends string = string> = {
  id: T;
  label: string;
  count: number;
};

/** KPI strip + tabbed detail table shell used by all statistics pages. */
export const StatsKpiListLayout: React.FC<{
  kpiGrid?: React.ReactNode;
  tabs: StatsListTab[];
  activeTab: string;
  onTabChange: (id: string) => void;
  children: React.ReactNode;
  charts?: React.ReactNode;
  /** Default `top`. `right` places charts beside the table. */
  chartsPlacement?: 'top' | 'right';
}> = ({ kpiGrid, tabs, activeTab, onTabChange, children, charts, chartsPlacement = 'top' }) => {
  const { colors } = useTheme();
  const {
    splitRef,
    panelWidth,
    panelResizing,
    startResize,
    minWidth,
    maxWidth,
  } = useResizablePanel({
    storageKey: 'crew-stats-charts-split',
    defaultWidth: 520,
    minWidth: 280,
    maxWidth: 960,
    side: 'right',
  });
  const tabBar = (
    <div
      className={cn(
        'shrink-0 inline-flex flex-wrap rounded-lg border p-0.5',
        colors.border,
        colors.surfaceHover,
      )}
    >
      {tabs.map((tab) => {
        const active = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              active
                ? cn(colors.surface, colors.text, 'shadow-sm')
                : cn(colors.textSecondary, 'hover:opacity-90'),
            )}
          >
            {tab.label}
            <span className={cn('ml-1.5 tabular-nums', active ? colors.text : colors.textMuted)}>
              {tab.count}
            </span>
          </button>
        );
      })}
    </div>
  );
  const tablePane = (
    <div className="flex-1 min-h-0 min-w-0 flex flex-col gap-2 overflow-hidden">
      {tabBar}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">{children}</div>
    </div>
  );
  const chartPane = charts ? (
    <div
      style={
        chartsPlacement === 'right'
          ? ({ ['--stats-chart-pane' as string]: `${panelWidth}px` } as React.CSSProperties)
          : undefined
      }
      className={cn(
        chartsPlacement === 'right'
          ? 'flex flex-col min-h-0 overflow-hidden w-full lg:w-[var(--stats-chart-pane)] lg:shrink-0'
          : 'shrink-0',
      )}
    >
      {charts}
    </div>
  ) : null;

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      {kpiGrid ? <div className="shrink-0">{kpiGrid}</div> : null}
      {chartsPlacement === 'right' && chartPane ? (
        <div
          ref={splitRef}
          className={cn(
            'flex-1 min-h-0 flex flex-col lg:flex-row gap-4 lg:gap-0 overflow-hidden',
            panelResizing && 'select-none',
          )}
        >
          {tablePane}
          <div className="hidden lg:flex">
            <ReferenceSidebarSplitter
              panelWidth={panelWidth}
              minWidth={minWidth}
              maxWidth={maxWidth}
              resizing={panelResizing}
              onMouseDown={startResize}
              label="Resize charts"
            />
          </div>
          {chartPane}
        </div>
      ) : (
        <>
          {chartPane}
          {tablePane}
        </>
      )}
    </div>
  );
};

export const StatsKpiButton: React.FC<{
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => (
  <button type="button" className="text-left w-full" onClick={onClick}>
    {children}
  </button>
);